import { Link, Typography } from "@hellocarbo/lasagne";
import { saveAs } from "file-saver";
import { Download } from "lucide-react";
import { use, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import bronzeMedal from "@/assets/fr/medals/simple/bronze.png";
import diamondMedal from "@/assets/fr/medals/simple/diamond.png";
import goldMedal from "@/assets/fr/medals/simple/gold.png";
import noMedal from "@/assets/fr/medals/simple/no-medal.svg";
import silverMedal from "@/assets/fr/medals/simple/silver.png";
import { UserContext } from "@/contexts";
import useSegmentEvent from "@/hooks/useSegmentEvent";
import { TMedalData } from "@/types";
import { renderMedalImage } from "@/utils/medal";

import BaseCard, { ActionButton } from "./BaseCard";

/*
 * Medals visuals don't contain the season and year: they are drawn on top of them (see renderMedalImage)
 */
export const medalUrlMap = {
  bronze: bronzeMedal,
  silver: silverMedal,
  gold: goldMedal,
  diamond: diamondMedal,
};

export const medalPercentileMap = {
  bronze: 30,
  silver: 15,
  gold: 5,
  diamond: 1,
};

const Medal = ({ medalData }: { medalData: TMedalData }) => {
  const { medal, season, year, any_medals } = medalData;
  const { t } = useTranslation("communicate");
  const { user } = use(UserContext);
  const [medalImageURL, setMedalImageURL] = useState<string>();
  const seasonLabel = t(`medal.badgeSeasons.${season}`);

  useEffect(() => {
    if (!medal) return;

    let ignore = false;
    renderMedalImage(medalUrlMap[medal], { season: seasonLabel, year })
      .then((image) => {
        if (!ignore) setMedalImageURL(image);
      })
      .catch((error) => {
        console.error("Failed to render the medal image", error);
        if (!ignore) setMedalImageURL(undefined);
      });

    return () => {
      ignore = true;
    };
  }, [medal, seasonLabel, year]);

  const downloadImage = () => {
    if (medalImageURL) {
      saveAs(medalImageURL, "medal.png");
    }
  };

  const { sendSegmentEvent } = useSegmentEvent();

  if (!medal && any_medals) {
    return (
      <BaseCard
        title={t("medal.withoutMedal.title")}
        text={t("medal.withoutMedal.text")}
        Icon={() => (
          <img
            src={noMedal}
            alt={t("medal.withoutMedal.IllustrationAlt")}
            className="size-[176px]"
          />
        )}
      >
        <Typography className="mb-sm mt-sm">
          {t("medal.withoutMedal.rankText", {
            season: t(`medal.seasons.${season}`),
            year,
          })}
        </Typography>

        <div className="mt-sm flex justify-between">
          <Link
            href={t("medal.helpCenterUrl")}
            className="my-sm"
            shouldOpenNewTab
          >
            {t("medal.helpCenterLink")}
          </Link>
        </div>
      </BaseCard>
    );
  } else if (medal) {
    return (
      <BaseCard
        title={t("medal.withMedal.title")}
        text={t("medal.withMedal.text", {
          percentile: medalPercentileMap[medal],
        })}
        Icon={() => (
          <img
            src={medalImageURL ?? medalUrlMap[medal]}
            crossOrigin="anonymous"
            alt={`${user?.company.name} - ${t("medal.withMedal.title")}`}
            className="size-[176px]"
          />
        )}
      >
        <Typography className="mb-sm mt-sm">
          {t("medal.withMedal.rankText", {
            season: t(`medal.seasons.${season}`),
            year,
            percentile: medalPercentileMap[medal],
          })}
        </Typography>
        <ActionButton
          as="button"
          disabled={!medalImageURL}
          onClick={() => {
            downloadImage();
            sendSegmentEvent("carbo_medal_downloaded");
          }}
        >
          <Download />
          {t("medal.withMedal.download")}
        </ActionButton>

        <div className="mt-sm flex justify-between">
          <Link
            href={t("medal.helpCenterUrl")}
            className="my-sm"
            shouldOpenNewTab
          >
            {t("medal.helpCenterLink")}
          </Link>
        </div>
      </BaseCard>
    );
  } else {
    return (
      <BaseCard
        title={t("medal.medalTeasing.title")}
        text={t("medal.medalTeasing.text")}
        Icon={() => (
          <img
            src={medalUrlMap.silver}
            alt={t("medal.medalTeasing.illustrationAlt")}
            className="size-[176px] blur-sm"
          />
        )}
        tag="teasing"
      />
    );
  }
};

export default Medal;
